import { server } from "../api";
import axios from "axios";

// ─── 共用設定 ────────────────────────────────────────────────────────────────
// 逾時預設 20 秒。後端正常回應都在 100ms 以內，超過 20 秒代表查詢卡住或服務異常，
// 與其讓瀏覽器一直轉圈（或等 nginx 的 60 秒 proxy_read_timeout 才吐 504），
// 不如提早中斷並給出明確訊息。
const DEFAULT_TIMEOUT = 20000;
const HEAVY_TIMEOUT = 30000; // 圖表、快照等資料量較大的請求

const http = axios.create({ timeout: DEFAULT_TIMEOUT });

/**
 * 把 axios error 轉成可以直接顯示給使用者的訊息。
 * 優先使用後端回傳的 message（例如「裝置座標無效」「找不到裝置」），
 * 這樣前端不必再自己猜原因。
 */
const describeError = (error, label) => {
  if (error?.code === "ECONNABORTED") {
    return `${label}：請求逾時，伺服器沒有在時限內回應`;
  }
  if (error?.code === "ERR_NETWORK" || !error?.response) {
    return `${label}：無法連線到伺服器`;
  }

  const { status, data } = error.response;

  // responseType 為 blob 時 data 是 Blob，沒有 message 欄位，會落到下面的 HTTP 狀態碼
  if (data && typeof data.message === "string" && data.message) {
    return `${label}：${data.message}`;
  }
  if (status === 504) {
    return `${label}：伺服器處理逾時 (504)`;
  }
  return `${label}：HTTP ${status}`;
};

/**
 * 統一的請求包裝。
 * 失敗時在 error 上掛一個 uiMessage，呼叫端可以直接 message.error(err.uiMessage)，
 * 不必再寫死籠統的錯誤字串。
 */
const request = async (config, label) => {
  try {
    const response = await http(config);
    return response.data;
  } catch (error) {
    const uiMessage = describeError(error, label);
    console.error(`[InfraredCamera] ${label} 失敗:`, error);
    if (error && typeof error === "object") {
      error.uiMessage = uiMessage;
    }
    throw error;
  }
};

/**
 * 取得紅外線鏡頭資料
 */
const GetData = async (machineID) =>
  request(
    { method: "get", url: `${server}api/infrared/${machineID}` },
    "載入鏡頭資料"
  );

/**
 * 更新參數設定（座標 + camera_sn）
 */
const UpdateSettings = async (machineID, data) =>
  request(
    { method: "post", url: `${server}api/infrared/${machineID}/settings`, data },
    "參數更新"
  );

/**
 * 取得附近氣象站清單（供選站地圖用）
 */
const GetNearbyStations = async (machineID, limit = 20) =>
  request(
    {
      method: "get",
      url: `${server}api/infrared/${machineID}/nearby-stations`,
      params: { limit },
    },
    "載入氣象站"
  );

/**
 * 設定自選氣象站（station_id 傳 null 則恢復自動最近站）
 */
const SelectStation = async (machineID, stationId) =>
  request(
    {
      method: "post",
      url: `${server}api/infrared/${machineID}/select-station`,
      data: { station_id: stationId },
    },
    "選站"
  );

/**
 * 取得圖表資料（歷史 + 預報 24hr）
 * @param {number} machineID
 * @param {'24h'|'72h'|'7d'|'30d'} range
 */
const GetChartData = async (machineID, { range, start, end } = { range: "72h" }) => {
  const params = start && end ? { start, end } : { range: range || "72h" };
  return request(
    {
      method: "get",
      url: `${server}api/infrared/${machineID}/chart-data`,
      params,
      timeout: HEAVY_TIMEOUT,
    },
    "載入圖表資料"
  );
};

/**
 * 取得氣象站資料（預留，未來實作）
 */
const GetWeatherData = async (machineID) =>
  request(
    { method: "get", url: `${server}api/infrared/${machineID}/weather` },
    "載入氣象資料"
  );

// ─── MSX 對位標定 ────────────────────────────────────────────────────────────

/**
 * 取得有 ch01/ch02 配對快照的日期清單（新到舊）
 */
const GetSnapshotDates = async (machineID) =>
  request(
    { method: "get", url: `${server}api/infrared/${machineID}/snapshots` },
    "載入快照日期"
  );

/**
 * 取得指定日期中兩通道都存在的時間清單（新到舊，格式 HHMMSS）
 */
const GetSnapshotTimes = async (machineID, date) =>
  request(
    {
      method: "get",
      url: `${server}api/infrared/${machineID}/snapshots`,
      params: { date },
    },
    "載入快照時間"
  );

/**
 * 取得單張快照的 Blob。
 * 用 Blob + objectURL 而非直接給 img src，可避免 canvas 被跨來源污染，
 * 讓標定預覽能安全呼叫 getImageData。
 * @param {'ch01'|'ch02'} channel
 * @param {string} date YYYY-MM-DD
 * @param {string} time HHMMSS
 */
const GetSnapshotBlob = async (machineID, channel, date, time) =>
  request(
    {
      method: "get",
      url: `${server}api/infrared/${machineID}/snapshot/${channel}/${date}/${time}`,
      responseType: "blob",
      timeout: HEAVY_TIMEOUT,
    },
    "載入快照影像"
  );

/**
 * 讀取 MSX 校正參數
 */
const GetCalibration = async (machineID) =>
  request(
    { method: "get", url: `${server}api/infrared/${machineID}/calibration` },
    "讀取校正參數"
  );

/**
 * 儲存 MSX 校正參數
 * @param {{coeffs:number[], points?:Array, opacity?:number, dx?:number, dy?:number, enabled?:number}} data
 */
const SaveCalibration = async (machineID, data) =>
  request(
    {
      method: "post",
      url: `${server}api/infrared/${machineID}/calibration`,
      data,
    },
    "儲存校正參數"
  );

export default {
  GetData,
  UpdateSettings,
  GetNearbyStations,
  SelectStation,
  GetChartData,
  GetWeatherData,
  GetSnapshotDates,
  GetSnapshotTimes,
  GetSnapshotBlob,
  GetCalibration,
  SaveCalibration,
};